import { v4 as uuid } from 'uuid';
import { toastConfig } from 'config';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { decodeQueryParamData } from 'utils/utils';
import { useNavigate, useParams } from 'react-router-dom';
import { useRef, useState, useEffect, useMemo } from 'react';
import { useNodesState, useEdgesState, MarkerType } from '@xyflow/react';
import { buildUpdatedWorkflowArray, conditionPayload } from '../utils/index';
import { useCreateWorkFlowDesignMutation, useGetWorkFlowDesignByIdMutation } from 'store/features/process/processApi';

const useEditWorkFlowHook = () => {
	const params = useParams();
	const navigate = useNavigate();
	const flowInstance = useRef(null);
	const reactFlowWrapper = useRef(null);

	const [nodes, setNodes, onNodesChange] = useNodesState([]);
	const [edges, setEdges, onEdgesChange] = useEdgesState([]);

	const [showModal, setShowModal] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedNode, setSelectedNode] = useState(null);
	const [selectedEdge, setSelectedEdge] = useState(null);
	const [activationStatus, setActivationStatus] = useState('');
	const [isEdgeModalOpen, setIsEdgeModalOpen] = useState(false);

	const { creatorOrganizationId, userId } = useSelector((state) => state.profile.profileData);
	const companyCode = creatorOrganizationId;

	const [getWorkFlowDesignById, { data: workFlowDesignData }] = useGetWorkFlowDesignByIdMutation();
	const [updateWorkFlowDesign, { isLoading, isSuccess, isError, error }] = useCreateWorkFlowDesignMutation();

	const onDrop = (event) => {
		event.preventDefault();

		const type = event.dataTransfer.getData('application/reactflow');
		if (!type) return;

		const bounds = reactFlowWrapper.current.getBoundingClientRect();

		// ⭐ updated correct function for v11+
		const position = flowInstance.current.screenToFlowPosition({
			x: event.clientX - bounds.left,
			y: event.clientY - bounds.top,
		});

		const newNode = {
			id: uuid(),
			type,
			position,
			data: {
				label: `${type}`,
				mode: 'both',
				onChangeMode: (newMode) => {
					setNodes((nds) => nds.map((n) => (n.id === newNode.id ? { ...n, data: { ...n.data, setNodes, mode: newMode } } : n)));
				},
			},
		};

		setNodes((nds) => nds.concat(newNode));
	};

	const onDragOver = (event) => {
		event.preventDefault();
		event.dataTransfer.dropEffect = 'move';
	};

	const onConnect = (params) => {
		const updateNodeList = nodes.map((node) => {
			if (node.id === params.source) {
				return {
					...node,
					data: {
						...node.data,
						hasChild: true,
					},
				};
			} else {
				return node;
			}
		});

		setNodes(updateNodeList);

		setEdges((eds) => [
			...eds,
			{
				...params,
				style: { strokeWidth: 2 },
				id: `${params.source}-${params.target}`,
				markerEnd: { type: MarkerType.ArrowClosed },
			},
		]);
	};

	const handleSubmit = () => {
		const designPayload = {
			nodes,
			edges,
		};

		const { solution, conditionMaster } = workFlowDesignData?.result ?? {};

		const solutionData = nodes?.find((item) => item?.type === 'solution')?.data ?? null;

		const solutionTaskList = nodes
			?.filter((item) => item?.type === 'solution_task')
			?.map((item) => ({ ...item?.data?.solutionTask, id: item?.data?.solutionTask?.solutionTaskId }));

		const taskConditionPeople = nodes
			?.filter((item) => item?.type === 'condition')
			?.map((item) => ({ taskActionTaken: item?.data?.taskActionTaken, conditionPeopleSet: item?.data?.conditionPeopleSet }));

		const convertJson = JSON.stringify(designPayload);
		const convertBase64 = btoa(convertJson);

		const payload = {
			id: state?.id,
			solution: {
				...solutionData?.solution,

				code: solutionData?.processCode,
				id: solutionData?.solution?.solutionId,
				version: solutionData?.processVersion,
				activationStatus: solution?.activationStatus,
			},
			companyCode,
			solutionTaskList,
			designBase64: convertBase64,
			// conditionMaster: {
			// 	...conditionMaster,
			// 	taskConditionPeople,
			// },
			conditionMaster: conditionPayload({
				conditionNodeList: nodes?.filter((item) => item?.type === 'condition'),
				conditionMaster: { ...conditionMaster, startTaskActionTaken: solutionTaskList?.[0]?.taskActionTaken },
			}),
			propertyArrayList: buildUpdatedWorkflowArray({
				taskList: solutionTaskList,
				existingResponse: workFlowDesignData?.result?.propertyArrayList,
			}),
		};

		// console.log(payload, 'payload');

		updateWorkFlowDesign(payload);
	};

	const handleUpdateSolution = () => {
		setIsModalOpen(true);

		const solution = nodes?.find((item) => item?.type === 'solution') ?? null;
		setSelectedNode(solution);
	};

	useEffect(() => {
		if (isSuccess) {
			toast.success('Success! Action completed.', toastConfig);

			navigate(`/comptroller/work-flow`);
		} else if (isError) {
			toast.error(error?.data?.message, toastConfig);
		}
	}, [isSuccess, isError]);

	const state = useMemo(() => {
		return decodeQueryParamData(params?.id);
	}, []);

	const dataDecode = useMemo(() => {
		if (workFlowDesignData?.result) {
			return decodeQueryParamData(workFlowDesignData?.result?.designBase64);
		} else {
			return null;
		}
	}, [workFlowDesignData]);

	useEffect(() => {
		if (state?.id) {
			getWorkFlowDesignById({ id: state?.id });
		}
	}, [state]);

	useEffect(() => {
		if (dataDecode && workFlowDesignData?.result) {
			const { solution, conditionMaster, solutionTaskList, activationStatus } = workFlowDesignData.result;

			setActivationStatus(activationStatus);

			const updatedNodes = dataDecode.nodes.map((node) => {
				let solutionTask = null;

				if (node.type === 'solution_task') {
					const taskCode = node?.data?.solutionTask?.taskCode;

					if (taskCode) {
						const findTask = solutionTaskList?.find((item) => item.taskCode === taskCode);

						if (findTask) {
							solutionTask = findTask;
						}
					}
				}

				return {
					...node,
					data: {
						...node.data,

						processCode: solution?.code,
						processVersion: solution?.version,
						solution: { ...node?.data?.solution, solutionId: solution?.id },
						conditionId: node.type === 'condition' ? conditionMaster?.id : node.data?.conditionId,
						solutionTask: {
							...solutionTask,
							...node?.data?.solutionTask,

							solutionTaskId: node.type === 'solution_task' ? solutionTask?.id : node.data?.solutionTaskId,
						},
					},
				};
			});

			setNodes(updatedNodes);
			setEdges(dataDecode.edges);
		}
	}, [dataDecode, workFlowDesignData]);

	return {
		nodes,
		edges,
		onDrop,
		setEdges,
		setNodes,
		isLoading,
		onConnect,
		showModal,
		onDragOver,
		isModalOpen,
		handleSubmit,
		setShowModal,
		flowInstance,
		selectedNode,
		selectedEdge,
		onNodesChange,
		onEdgesChange,
		setIsModalOpen,
		setSelectedNode,
		isEdgeModalOpen,
		setSelectedEdge,
		activationStatus,
		reactFlowWrapper,
		setIsEdgeModalOpen,
		handleUpdateSolution,
	};
};

export default useEditWorkFlowHook;
